import * as React from 'react';

import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';

import { alpha, useTheme } from '@mui/material/styles';

import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';

import apiKIA from '../../services/apiKIA';

const SUBSYSTEM_ENDPOINT = '/api/sgc';

function formatTime(date) {
  try {
    const parsed = date instanceof Date ? date : new Date(date);
    const hours = parsed.getHours().toString().padStart(2, '0');
    const minutes = parsed.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch {
    return '';
  }
}

export default function VirtualAssistant({ profile }) {
  const theme = useTheme();

  const [messages, setMessages] = React.useState([
    {
      id: 1,
      text: 'Olá! Como posso ajudar você hoje?',
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);

  const [inputText, setInputText] = React.useState('');
  const [isTyping, setIsTyping] = React.useState(false);

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');

  const [clearDialogOpen, setClearDialogOpen] = React.useState(false);

  const listRef = React.useRef(null);

  const originUserId = 'admin-test-a3d3fac3-bc9e-4c6c-bf27-21d313dc8dd4';

  const scrollToBottom = React.useCallback(() => {
    const element = listRef.current;
    if (!element) return;
    element.scrollTop = element.scrollHeight;
  }, []);

  React.useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = async () => {
    const trimmed = inputText.trim();
    if (!trimmed) return;

    const userMessage = {
      id: Date.now(),
      text: trimmed,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText('');
    setIsTyping(true);

    const payload = {
      originUserId,
      query: trimmed,
      subSystemEndpoint: SUBSYSTEM_ENDPOINT,
    };

    try {
      const response = await apiKIA.post('/api/chat', payload);

      const botText = response?.data?.response || 'Desculpe, não consegui processar sua pergunta.';
      const botMessage = {
        id: Date.now() + 1,
        text: botText,
        sender: 'bot',
        timestamp: new Date(),
        metadata: response?.data?.metadata,
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erro ao chamar K-IA:', error);

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 2,
          text: 'Desculpe, houve um erro ao processar sua mensagem. Por favor, tente novamente.',
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);

      setSnackbarMessage('Erro ao conectar com o assistente virtual.');
      setSnackbarOpen(true);
    } finally {
      setIsTyping(false);
    }
  };

  const handleConfirmClearHistory = async () => {
    try {
      await apiKIA.delete(`/api/chat/${encodeURIComponent(originUserId)}`);
      setMessages([
        {
          id: 1,
          text: 'Olá! Como posso ajudar você hoje?',
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);
      setSnackbarMessage('Histórico limpo com sucesso!');
      setSnackbarOpen(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erro ao limpar histórico:', error);
      setSnackbarMessage('Erro ao limpar o histórico.');
      setSnackbarOpen(true);
    } finally {
      setClearDialogOpen(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: 'calc(100vh - 56px)',
        display: 'flex',
        justifyContent: 'center',
        padding: { xs: 1, sm: 2 },
        boxSizing: 'border-box',
      }}
    >
      <Paper
        elevation={2}
        sx={{
          width: '100%',
          maxWidth: 980,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 2,
          backgroundColor: '#fff',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            padding: 2,
          }}
        >
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            <SmartToyIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, color: '#323232' }}>K-IA</Typography>
            <Typography sx={{ fontSize: 13, color: '#737373' }}>Assistente Virtual</Typography>
            <Typography sx={{ fontSize: 12, color: '#999' }} noWrap>
              {profile?.name ? `Usuário: ${profile.name}` : `Usuário: ${originUserId}`}
            </Typography>
          </Box>
          <Button
            variant="text"
            color="inherit"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setClearDialogOpen(true)}
            sx={{ color: '#737373', textTransform: 'none' }}
          >
            Limpar
          </Button>
        </Box>

        <Divider sx={{ backgroundColor: theme.palette.primary.main }} />

        <Box
          ref={listRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            backgroundColor: '#f5f5f5',
            padding: { xs: 1.5, sm: 2 },
          }}
        >
          {messages.map((message) => {
            const isUser = message.sender === 'user';
            return (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: isUser ? 'flex-end' : 'flex-start',
                  marginBottom: 1.5,
                  alignItems: 'flex-end',
                  gap: 1,
                }}
              >
                {!isUser ? (
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                    <SmartToyIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                ) : null}

                <Box
                  sx={{
                    maxWidth: 'min(720px, 78%)',
                    backgroundColor: isUser ? theme.palette.primary.main : '#fff',
                    borderRadius: 2,
                    padding: 1.25,
                    boxShadow: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 15,
                      color: isUser ? '#fff' : '#323232',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {message.text}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#999', marginTop: 0.5, textAlign: 'right' }}>
                    {formatTime(message.timestamp)}
                  </Typography>
                </Box>

                {isUser ? (
                  <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: 32, height: 32 }}>
                    <PersonIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                ) : null}
              </Box>
            );
          })}

          {isTyping ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                marginBottom: 1.5,
                alignItems: 'flex-end',
                gap: 1,
              }}
            >
              <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                <SmartToyIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box
                sx={{
                  maxWidth: 'min(720px, 78%)',
                  backgroundColor: '#fff',
                  borderRadius: 2,
                  padding: 1.25,
                  boxShadow: 1,
                }}
              >
                <Typography sx={{ fontSize: 14, color: '#999', fontStyle: 'italic' }}>
                  K-IA está digitando...
                </Typography>
              </Box>
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            padding: 1.5,
            backgroundColor: '#fff',
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            gap: 1,
            alignItems: 'stretch',
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={4}
            minRows={1}
            size="small"
            placeholder="Digite sua mensagem..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
              },
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!inputText.trim() || isTyping}
            sx={{
              alignSelf: 'stretch',
              width: 44,
              borderRadius: 1,
              bgcolor: theme.palette.primary.main,
              color: '#fff',
              '&:hover': { bgcolor: theme.palette.primary.dark },
              '&.Mui-disabled': { bgcolor: alpha(theme.palette.primary.main, 0.35), color: '#fff' },
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity="info" sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <Dialog
          open={clearDialogOpen}
          onClose={() => setClearDialogOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
            Limpar histórico
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: '#666' }}>
              Deseja realmente limpar todo o histórico de conversa?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ paddingX: 3, paddingBottom: 2 }}>
            <Button
              onClick={() => setClearDialogOpen(false)}
              variant="outlined"
              color="secondary"
              sx={{ textTransform: 'none' }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmClearHistory}
              variant="contained"
              color="primary"
              sx={{ textTransform: 'none' }}
            >
              Limpar
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
}
